import { useMemo } from 'react';
import type { EventTypeAssetCount } from '@/modules/pipelines/data/metrics';
import styles from '@/modules/pipelines/components/deep-dive/asset-metrics/asset-event-type-table.module.scss';

interface Row extends EventTypeAssetCount {
  isOther?: boolean;
}

export default function AssetEventTypeTable({
  eventTypes,
  windowLabel,
  limit = 8,
}: {
  eventTypes: EventTypeAssetCount[];
  windowLabel: string;
  limit?: number;
}) {
  const rows = useMemo<Row[]>(() => {
    if (eventTypes.length <= limit + 1) return eventTypes;

    const head = eventTypes.slice(0, limit);
    const tail = eventTypes.slice(limit);

    return [
      ...head,
      {
        eventType: `Other (${tail.length} types)`,
        notices: tail.reduce((sum, row) => sum + row.notices, 0),
        assets: tail.reduce((sum, row) => sum + row.assets, 0),
        isOther: true,
      },
    ];
  }, [eventTypes, limit]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <span className={styles.title}>Notices by event type</span>
        <span className={styles.subtitle}>
          {eventTypes.length ? `${eventTypes.length} types · ${windowLabel}` : windowLabel}
        </span>
      </div>

      {!eventTypes.length && (
        <div className={styles.empty}>No notices matched an asset in this window.</div>
      )}

      {rows.length > 0 && (
        <div className={styles.tableScroll}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Event type</th>
                <th className={styles.numeric}>Notices</th>
                <th className={styles.numeric}>Assets</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.eventType}>
                  <td
                    className={`${styles.name} ${row.isOther ? styles.other : ''}`}
                    title={row.eventType}
                  >
                    {row.eventType}
                  </td>
                  <td className={`${styles.numeric} ${styles.count}`}>
                    {row.notices.toLocaleString()}
                  </td>
                  <td className={`${styles.numeric} ${styles.count}`}>
                    {row.assets.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
